import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Drawer from "@mui/material/Drawer";
import Tooltip from "@mui/material/Tooltip";
import CloseRounded from "@mui/icons-material/CloseRounded";
import InsertDriveFileOutlined from "@mui/icons-material/InsertDriveFileOutlined";
import OpenInNewRounded from "@mui/icons-material/OpenInNewRounded";
import VisibilityOutlined from "@mui/icons-material/VisibilityOutlined";

import { formatDate } from "../../_src_shared/utils/functions/formatDate";
import {
  APPRAISAL_OUTCOME_LABEL,
  AppraisalStatusChip,
  orderAppraisalDocOids,
} from "./appraisalOrderUi";
import JsonPreview from "../common/JsonPreview";
import { useState } from "react";
import { CodeRounded } from "@mui/icons-material";

// ObjectIds arrive as plain strings or Mongo extended-JSON ({ $oid }).
const oidStr = (v) => {
  if (v == null) return null;
  if (typeof v === "object") return v.$oid || v._id || null;
  return String(v);
};

/* Palette + primitives ported verbatim from the Lock Queue's LockInfoPane so
   the appraisal detail pane reads identically (section labels, KV rows). */
const C = {
  surface2: "#f7f9fa",
  text: "#1f2430",
  text2: "#5d6675",
  text3: "#9aa3b2",
  border: "#e6e9ee",
  border2: "#d4d9e1",
  navy: "#1f2a3c",
  navyHover: "#2a384e",
  radiusSm: "7px",
};
const MONO = "ui-monospace,SFMono-Regular,Menlo,Consolas,monospace";

const fmt = (v) => (v ? formatDate(v, "") : null);

const InfoLabel = ({ children }) => (
  <Box
    sx={{
      fontSize: "10.5px",
      fontWeight: 700,
      letterSpacing: ".05em",
      textTransform: "uppercase",
      mt: "26px",
      mb: "4px",
    }}
  >
    {children}
  </Box>
);

const KV = ({ k, v, strong, mono, empty }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: "12px",
      py: "7px",
      borderBottom: `0.5px solid ${C.border}`,
    }}
  >
    <Box sx={{ fontSize: 10.5, color: C.text2, flexShrink: 0 }}>{k}</Box>
    <Box
      sx={{
        fontSize: 10.5,
        color: empty ? C.text3 : C.text,
        fontWeight: strong ? 600 : 400,
        fontFamily: mono ? MONO : "inherit",
        textAlign: "right",
        minWidth: 0,
        wordBreak: "break-word",
      }}
    >
      {empty ? "—" : v}
    </Box>
  </Box>
);

const NoteBlock = ({ children }) => (
  <Box
    sx={{
      fontSize: 11,
      color: C.text2,
      lineHeight: 1.55,
      whiteSpace: "pre-wrap",
      background: C.surface2,
      borderRadius: C.radiusSm,
      p: "10px 11px",
      mt: "8px",
    }}
  >
    {children}
  </Box>
);

const AppraisalDocCard = ({ doc, loading, onPreview, previewing }) => {
  const enabled = Boolean(doc && onPreview && !previewing && !loading);
  return (
    <Box
      component="button"
      type="button"
      disabled={!enabled}
      onClick={enabled ? onPreview : undefined}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "10px",
        width: "100%",
        p: "10px 11px",
        background: C.surface2,
        border: "none",
        borderRadius: C.radiusSm,
        mt: "8px",
        font: "inherit",
        textAlign: "left",
        cursor: enabled ? "pointer" : "default",
        opacity: doc || loading ? 1 : 0.75,
        "&:hover": enabled ? { background: "#eef1f4" } : {},
      }}
    >
      <InsertDriveFileOutlined
        sx={{ fontSize: 16, color: "#0f6e56", flexShrink: 0 }}
      />
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Box
          sx={{
            fontSize: "10.5px",
            fontWeight: 600,
            color: C.text,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {doc?.file_display_name ||
            doc?.file_name_as_loaded ||
            "Appraisal document"}
        </Box>
        <Box sx={{ fontSize: 10, color: C.text3 }}>
          {loading
            ? "Loading document…"
            : doc
              ? "Click to preview"
              : "Not found on the loan"}
        </Box>
      </Box>
      {previewing ? (
        <CircularProgress size={14} />
      ) : (
        <VisibilityOutlined
          sx={{ fontSize: 16, color: C.text2, flexShrink: 0 }}
        />
      )}
    </Box>
  );
};

/**
 * AppraisalOrderDetailContent — single-order detail, styled to match the Lock
 * Queue's LockInfoPane. Used inside the Drawer (loan Orders tab) and as the
 * middle column of the queue's split view.
 *   - `onClose` renders a header close button (drawer only).
 *   - `actions` renders just below the header (above the first divider).
 *   - `appraisalDocs` (loan-document objects, one per id on the order — resolve
 *     with `orderAppraisalDocOids(order)`) + `onPreviewAppraisal(doc)` enable
 *     the per-document preview; `previewingAppraisal` is the oid currently
 *     being fetched (or `true` for all). The legacy singular `appraisalDoc`
 *     prop still works for a one-document caller.
 */
export const AppraisalOrderDetailContent = ({
  order,
  showLoanLink = true,
  onClose,
  actions = null,
  appraisalDoc,
  appraisalDocs,
  docLoading = false,
  onPreviewAppraisal,
  previewingAppraisal = false,
}) => {
  const [showJson, setShowJson] = useState(false);
  if (!order) return null;
  const completed = order.status === "completed";
  const cancelled = order.status === "cancelled";

  // One card per appraisal on the order. Match each id to its resolved loan
  // document (the caller looks them up on the loan); an id with no match still
  // renders so the operator sees it was on the order.
  const docOids = orderAppraisalDocOids(order);
  const resolvedDocs = Array.isArray(appraisalDocs)
    ? appraisalDocs
    : appraisalDoc
      ? [appraisalDoc]
      : [];
  const docFor = (oid) =>
    resolvedDocs.find((d) => d && oidStr(d._id) === oid) || null;
  const isPreviewing = (oid) =>
    previewingAppraisal === true || previewingAppraisal === oid;

  return (
    <Box
      sx={{
        // p: "16px 18px",
        display: "flex",
        flexDirection: "column",
        height: "100%",
      }}
    >
      {/* Header */}
      <Box sx={{ flexShrink: 0, p: "16px 18px" }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: "10px",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "9px",
              minWidth: 0,
              flexWrap: "wrap",
            }}
          >
            <Box
              sx={{
                fontSize: 19,
                fontWeight: 600,
                color: C.text,
                whiteSpace: "nowrap",
              }}
            >
              {order.loanNumber != null ? `#${order.loanNumber}` : "Appraisal"}
            </Box>
            <AppraisalStatusChip status={order.status} />
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "6px",
              flexShrink: 0,
            }}
          >
            <Tooltip title={showJson ? "Hide raw JSON" : "View raw JSON"} arrow>
              <Box
                component="button"
                type="button"
                onClick={() => setShowJson((v) => !v)}
                aria-label="Toggle raw JSON"
                sx={{
                  width: 26,
                  height: 24,
                  flexShrink: 0,
                  borderRadius: C.radiusSm,
                  border: `0.5px solid ${showJson ? C.navy : C.border2}`,
                  background: showJson ? C.navy : "#fff",
                  color: showJson ? "#fff" : C.text2,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  "&:hover": {
                    background: showJson ? C.navyHover : C.surface2,
                  },
                }}
              >
                <CodeRounded sx={{ fontSize: 14 }} />
              </Box>
            </Tooltip>
            {/* {showLoanLink && order?.dealId && ( */}
            {order?.dealId && (
              <Tooltip title="Open loan in new tab" arrow>
                <Box
                  component="a"
                  href={`/loan/${order.dealId}`}
                  target="_blank"
                  rel="noreferrer"
                  aria-label="Open loan in new tab"
                  sx={{
                    width: 26,
                    height: 24,
                    flexShrink: 0,
                    borderRadius: C.radiusSm,
                    border: `0.5px solid ${C.border2}`,
                    background: "#fff",
                    color: C.text2,
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    textDecoration: "none",
                    "&:hover": { background: C.surface2 },
                  }}
                >
                  <OpenInNewRounded sx={{ fontSize: 14 }} />
                </Box>
              </Tooltip>
            )}
            {onClose && (
              <Box
                component="button"
                type="button"
                onClick={onClose}
                aria-label="Close"
                sx={{
                  width: 26,
                  height: 24,
                  flexShrink: 0,
                  borderRadius: C.radiusSm,
                  border: `0.5px solid ${C.border2}`,
                  background: "#fff",
                  color: C.text2,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  "&:hover": { background: C.surface2 },
                }}
              >
                <CloseRounded sx={{ fontSize: 15 }} />
              </Box>
            )}
          </Box>
        </Box>

        <Box sx={{ fontSize: 12, color: C.text2, mt: "8px", lineHeight: 1.5 }}>
          Internal Appraisal Review ·{" "}
          {order.subjectPropertyAddress || "Subject property"}
        </Box>

        {/* Action buttons — below the loan number, above the divider */}
        {actions && <Box sx={{ mt: "12px" }}>{actions}</Box>}
      </Box>

      {/* Divider */}
      <Box
        sx={{ height: "1px", background: C.border, mx: "13px", flexShrink: 0 }}
      />

      {/* Scrollable body */}
      {showJson ? (
        <Box sx={{ mt: "14px", p: 2 }}>
          <JsonPreview values={order} sticky={false} />
        </Box>
      ) : (
        <Box
          sx={{
            flex: "1 1 0",
            minHeight: 0,
            overflowY: "auto",
            p: "16px 18px",
            pt: 1,
          }}
        >
          {docOids.length > 0 && (
            <>
              <InfoLabel>
                {docOids.length === 1
                  ? "Appraisal document"
                  : `Appraisal documents (${docOids.length})`}
              </InfoLabel>
              {docOids.map((oid) => {
                const doc = docFor(oid);
                return (
                  <AppraisalDocCard
                    key={oid}
                    doc={doc}
                    loading={docLoading}
                    onPreview={
                      onPreviewAppraisal && doc
                        ? () => onPreviewAppraisal(doc)
                        : undefined
                    }
                    previewing={isPreviewing(oid)}
                  />
                );
              })}
            </>
          )}

          <InfoLabel>Request</InfoLabel>
          <KV
            k="Loan number"
            v={order.loanNumber != null ? `#${order.loanNumber}` : null}
            empty={order.loanNumber == null}
          />
          <KV
            k="Requested by"
            v={order.requestedBy}
            empty={!order.requestedBy}
          />
          <KV
            k="Requested-by date"
            v={fmt(order.requestedByDate)}
            empty={!order.requestedByDate}
          />
          <KV k="Created" v={fmt(order.createdAt)} empty={!order.createdAt} />
          <KV
            k="Last updated"
            v={fmt(order.updatedAt)}
            empty={!order.updatedAt}
          />

          <InfoLabel>Assignment &amp; outcome</InfoLabel>
          <KV k="Assigned to" v={order.assignedTo} empty={!order.assignedTo} />
          <KV
            k="Assigned at"
            v={fmt(order.assignedAt)}
            empty={!order.assignedAt}
          />
          {completed && (
            <>
              <KV
                k="Outcome"
                v={
                  order.reviewOutcome
                    ? APPRAISAL_OUTCOME_LABEL[order.reviewOutcome] ||
                      order.reviewOutcome
                    : null
                }
                strong
                empty={!order.reviewOutcome}
              />
              <KV
                k="Completed by"
                v={order.completedBy}
                empty={!order.completedBy}
              />
              <KV
                k="Completed at"
                v={fmt(order.completedAt)}
                empty={!order.completedAt}
              />
            </>
          )}
          {cancelled && (
            <>
              <KV
                k="Cancelled by"
                v={order.cancelledBy}
                empty={!order.cancelledBy}
              />
              <KV
                k="Cancelled at"
                v={fmt(order.cancelledAt)}
                empty={!order.cancelledAt}
              />
              <KV
                k="Reason"
                v={order.cancelReason}
                empty={!order.cancelReason}
              />
            </>
          )}

          {order.instructions && (
            <>
              <InfoLabel>Instructions</InfoLabel>
              <NoteBlock>{order.instructions}</NoteBlock>
            </>
          )}

          {order.reviewNotes && (
            <>
              <InfoLabel>Review notes</InfoLabel>
              <NoteBlock>{order.reviewNotes}</NoteBlock>
            </>
          )}

          <InfoLabel>Notification</InfoLabel>
          <KV k="Email sent" v={order.emailSent ? "Yes" : "No"} />
          <KV
            k="Sent as"
            v={order.emailSentAs}
            mono
            empty={!order.emailSentAs}
          />
          <KV
            k="Notified"
            v={
              Array.isArray(order.notifiedRecipients) &&
              order.notifiedRecipients.length
                ? order.notifiedRecipients.join(", ")
                : null
            }
            empty={
              !Array.isArray(order.notifiedRecipients) ||
              !order.notifiedRecipients.length
            }
          />

          {Array.isArray(order.history) && order.history.length > 0 && (
            <>
              <InfoLabel>History</InfoLabel>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "10px",
                  mt: "4px",
                }}
              >
                {order.history.map((h, i) => (
                  <Box key={i} sx={{ display: "flex", gap: "10px" }}>
                    <Box
                      sx={{
                        width: 7,
                        height: 7,
                        borderRadius: "50%",
                        background: C.border2,
                        mt: "4px",
                        flexShrink: 0,
                      }}
                    />
                    <Box sx={{ minWidth: 0 }}>
                      <Box
                        sx={{
                          fontSize: 11,
                          fontWeight: 600,
                          color: C.text,
                          textTransform: "capitalize",
                        }}
                      >
                        {(h.status || "").replace(/_/g, " ")}
                      </Box>
                      <Box sx={{ fontSize: 10, color: C.text3 }}>
                        {[fmt(h.at), h.by].filter(Boolean).join(" · ")}
                      </Box>
                      {h.note && (
                        <Box sx={{ fontSize: 10.5, color: C.text2, mt: "2px" }}>
                          {h.note}
                        </Box>
                      )}
                    </Box>
                  </Box>
                ))}
              </Box>
            </>
          )}
        </Box>
      )}
    </Box>
  );
};

/**
 * Drawer wrapper around the detail content — used by the loan Orders tab's
 * "View" action.
 */
const AppraisalOrderDetailDrawer = ({
  order,
  open,
  onClose,
  showLoanLink = true,
  actions = null,
  appraisalDoc,
  appraisalDocs,
  docLoading,
  onPreviewAppraisal,
  previewingAppraisal,
}) => (
  <Drawer
    anchor="right"
    open={open}
    onClose={onClose}
    sx={{ zIndex: 13400 }}
    PaperProps={{ sx: { width: { xs: "100%", sm: 440 }, maxWidth: "100%" } }}
  >
    {order && (
      <AppraisalOrderDetailContent
        order={order}
        onClose={onClose}
        showLoanLink={showLoanLink}
        actions={actions}
        appraisalDoc={appraisalDoc}
        appraisalDocs={appraisalDocs}
        docLoading={docLoading}
        onPreviewAppraisal={onPreviewAppraisal}
        previewingAppraisal={previewingAppraisal}
      />
    )}
  </Drawer>
);

export default AppraisalOrderDetailDrawer;
